import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Chip,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Clear as ClearIcon,
  Add as AddIcon
} from '@mui/icons-material';

const MapboxDestinationSearch = ({ 
  destinations = [], 
  onDestinationsChange, 
  placeholder = "Search for destinations...",
  maxDestinations = 5
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const timeoutRef = useRef(null);
  const searchBoxRef = useRef(null);

  // const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
  const MAPBOX_TOKEN = 'sk.eyJ1IjoidnZsbGwyZmxyaSIsImEiOiJjbWthajBoM3AyNWJuM2VwdHRqYzh1djZzIn0.uuQQJvrKeBq8zv38EWFXYA';

  // Debounced search function
  const searchPlaces = async (query) => {
    if (!query.trim() || !MAPBOX_TOKEN || MAPBOX_TOKEN === 'your_mapbox_token_here') {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?` +
        `access_token=${MAPBOX_TOKEN}&` +
        `types=place,locality,neighborhood,address,poi&` +
        `limit=5`
      );

      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.features || []);
      } else {
        console.error('Mapbox search error:', response.statusText);
        setSuggestions([]);
      }
    } catch (error) {
      console.error('Error searching places:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle search input change with debouncing
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      searchPlaces(searchValue);
    }, 300);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [searchValue]);

  // Handle input change
  const handleInputChange = (event) => {
    const value = event.target.value;
    setSearchValue(value);
    setShowSuggestions(true);
  };

  // Handle suggestion selection
  const handleSuggestionSelect = (feature) => {
    const newDestination = {
      id: feature.id || Date.now(),
      name: feature.place_name,
      shortName: feature.text,
      coordinates: feature.geometry.coordinates,
      placeType: feature.place_type?.[0] || 'place',
      context: feature.context || []
    };

    // Check if destination already exists
    const exists = destinations.some(dest => 
      dest.coordinates[0] === newDestination.coordinates[0] && 
      dest.coordinates[1] === newDestination.coordinates[1]
    );

    if (!exists && destinations.length < maxDestinations) {
      onDestinationsChange([...destinations, newDestination]);
    }

    // Clear search
    setSearchValue('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  // Remove destination
  const handleRemoveDestination = (destinationId) => {
    onDestinationsChange(destinations.filter(dest => dest.id !== destinationId));
  };

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get place icon based on type
  const getPlaceIcon = (placeType) => {
    switch (placeType) {
      case 'poi':
        return '🏛️';
      case 'place':
      case 'locality':
        return '🏙️';
      case 'neighborhood':
        return '🏘️';
      case 'address':
        return '📍';
      default:
        return '📍';
    }
  };

  return (
    <Box ref={searchBoxRef} sx={{ position: 'relative', width: '100%' }}>
      {/* Selected Destinations */}
      {destinations.length > 0 && (
        <Box sx={{ 
          marginBottom: '15px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px'
        }}>
          {destinations.map((destination) => (
            <Chip
              key={destination.id}
              label={destination.shortName}
              onDelete={() => handleRemoveDestination(destination.id)}
              deleteIcon={<ClearIcon />}
              sx={{
                backgroundColor: '#FF7B6B',
                color: 'white',
                '& .MuiChip-deleteIcon': {
                  color: 'rgba(255, 255, 255, 0.8)',
                  '&:hover': {
                    color: 'white'
                  }
                },
                '&:hover': {
                  backgroundColor: '#FF6B5B'
                }
              }}
            />
          ))}
        </Box>
      )}

      {/* Search Input */}
      <TextField
        fullWidth
        value={searchValue}
        onChange={handleInputChange}
        onFocus={() => setShowSuggestions(true)}
        placeholder={destinations.length >= maxDestinations ? 
          `Maximum ${maxDestinations} destinations reached` : 
          placeholder
        }
        disabled={destinations.length >= maxDestinations}
        InputProps={{
          startAdornment: (
            <LocationIcon sx={{ 
              color: '#FF7B6B', 
              marginRight: '8px' 
            }} />
          ),
          endAdornment: loading ? (
            <CircularProgress size={20} sx={{ color: '#FF7B6B' }} />
          ) : null,
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'white',
            borderRadius: '12px',
            '&:hover fieldset': {
              borderColor: '#FF7B6B',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#FF7B6B',
              borderWidth: '2px'
            }
          },
          '& .MuiInputLabel-root': {
            '&.Mui-focused': {
              color: '#FF7B6B',
            }
          }
        }}
      />

      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <Paper
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1000,
            maxHeight: '300px',
            overflow: 'auto',
            marginTop: '4px',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(255, 123, 107, 0.1)'
          }}
        >
          <List sx={{ padding: 0 }}>
            {suggestions.map((feature, index) => (
              <ListItem
                key={feature.id || index}
                component="div"
                onClick={() => handleSuggestionSelect(feature)}
                sx={{
                  cursor: 'pointer',
                  padding: '12px 16px',
                  borderBottom: index < suggestions.length - 1 ? 
                    '1px solid rgba(0, 0, 0, 0.05)' : 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 123, 107, 0.05)'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: '40px' }}>
                  <Typography sx={{ fontSize: '20px' }}>
                    {getPlaceIcon(feature.place_type?.[0])}
                  </Typography>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography sx={{ 
                      fontWeight: 600,
                      color: '#4A5568',
                      fontSize: '0.95rem'
                    }}>
                      {feature.text}
                    </Typography>
                  }
                  secondary={
                    <Typography sx={{ 
                      color: '#666',
                      fontSize: '0.85rem',
                      marginTop: '2px'
                    }}>
                      {feature.place_name}
                    </Typography>
                  }
                />
                <IconButton size="small" sx={{ color: '#FF7B6B' }}>
                  <AddIcon />
                </IconButton>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {/* No token warning */}
      {(!MAPBOX_TOKEN || MAPBOX_TOKEN === 'your_mapbox_token_here') && (
        <Typography sx={{ 
          color: '#FF7B6B', 
          fontSize: '0.8rem', 
          marginTop: '8px',
          fontStyle: 'italic'
        }}>
          Please add your Mapbox access token to enable place search
        </Typography>
      )}

      {/* Destination count info */}
      <Typography sx={{ 
        color: '#666', 
        fontSize: '0.8rem', 
        marginTop: '8px'
      }}>
        {destinations.length}/{maxDestinations} destinations selected
      </Typography>
    </Box>
  );
};

export default MapboxDestinationSearch;